import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { PorangeButton } from "@/components/atoms/PorangeButton";

const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const EmailScreen = ({ 
  data, 
  onAnswer, 
  isTransitioning,
  direction,
}) => {
  const [email, setEmail] = useState(data.savedEmail || '');
  const [touched, setTouched] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const inputRef = useRef(null);

  const isValid = validateEmail(email);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (!isTransitioning) {
      setTimeout(() => setShowContent(true), 100);
    } else {
      setShowContent(false);
    }
  }, [isTransitioning]);

  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        const form = document.querySelector('form');
        if (form) {
          const heightDiff = window.innerHeight - window.visualViewport.height;
          if (heightDiff > 100) {
            form.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    return () => window.visualViewport?.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setAttempted(true);
    
    if (isValid && !isTransitioning) {
      onAnswer({ text: email });
    }
  };

  const getBorderColor = () => {
    if (!touched) return 'border-gray-300';
    return isValid ? 'border-green-500' : 'border-red-500';
  };

  const handleBlur = () => {
    setTouched(true);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (!touched) setTouched(true);
  };

  return (
    <div 
      className={classNames(
        "w-full min-h-[100vh]",
        "overflow-y-auto",
        { "pointer-events-none": isTransitioning }
      )}
    >
      <div className={classNames(
        "w-full pb-6",
        { 
          "pointer-events-none": isTransitioning,
          "transition-all duration-500 ease-in-out": isTransitioning,
          "translate-x-full opacity-0": isTransitioning && direction === 'back',
          "-translate-x-full opacity-0": isTransitioning && direction === 'forward',
        }
      )}>
        <div className="px-5 pt-6 pb-1">
          <h2 
            className={classNames(
              "text-2xl text-gray-700 font-semibold text-center",
              "transition-all duration-500 ease-out",
              {
                "opacity-0 translate-y-4": !showContent,
                "opacity-100 translate-y-0": showContent,
                "mb-6": !data.subtext,
                "mb-2": data.subtext
              }
            )}
          >
            {data.text.split('\n').map((line, index, array) => (
              <React.Fragment key={index}>
                {line}
                {index < array.length - 1 && <br />}
              </React.Fragment>
            ))}
          </h2>
        </div>
        <div className="px-5 space-y-3">
          <form onSubmit={handleSubmit} className="space-y-3">
            <div
              className={classNames(
                "transition-all duration-500 ease-out",
                {
                  "opacity-0 translate-y-4": !showContent,
                  "opacity-100 translate-y-0": showContent
                }
              )}
            >
              <label htmlFor="email" className="block text-base text-gray-700 font-normal mb-4">
                Email address
              </label>
              <div className="relative">
                <input
                  ref={inputRef}
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleBlur}
                  className={`w-full p-4 pr-[120px] rounded-xl bg-white border ${getBorderColor()} 
                    text-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-pink-500/20 h-[60px]
                    placeholder:text-gray-500`}
                  placeholder="Enter your email"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <button
                    type="submit"
                    className={classNames(
                      "px-6 h-[44px] rounded-lg text-white font-medium",
                      "bg-gradient-dark-porange hover:bg-gradient-darker-porange active:bg-gradient-darker-porange",
                      "transition-all duration-300 ease-out",
                      {
                        "opacity-40 cursor-not-allowed": !isValid,
                        "opacity-100": isValid
                      }
                    )}
                    disabled={!isValid || isTransitioning}
                  >
                    Continue
                  </button>
                </div>
              </div>
              <div className="mt-4 text-[15px] text-gray-600">
                By clicking "Continue", you agree to Atly's{' '}
                <a href="https://www.atly.com/terms-of-use" className="text-pink-500 hover:text-pink-600 underline">
                  Terms of Service
                </a>
                {' '}and{' '}
                <a href="https://www.atly.com/privacy-policy" className="text-pink-500 hover:text-pink-600 underline">
                  Privacy Policy
                </a>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}; 